import { Icon } from "@opendash/icons";
import {
  Descriptions,
  Divider,
  Drawer,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { ExportButton } from "../../../components/ExportButton";
import { ImageGallery } from "../../../components/ImageGallery";
import { getUsername } from "../../../helper/getUsername";
import {
  SIV_DeliveryNotes,
  SIV_GoodsReceiptProcess,
  SIV_GoodsReceiving,
  SIV_GoodsReceivingItem,
  SIV_Package,
} from "../../../parsegenerated";
import { DeliveryNoteDetailDrawer } from "../../deliveryNotes";
import { GoodsReceiptProcessPositionsDrawer } from "../../goodsReceiptProcess";
import { PackageDetailDrawer } from "../../packages";
import {
  useGoodsReceivingItemEnrichment,
  useGoodsReceivingItems,
} from "../hooks/useGoodsReceiving";
import { GoodsReceivingFlowChart } from "./GoodsReceivingFlowChart";

type Props = {
  goodsReceiving: SIV_GoodsReceiving | null;
  open: boolean;
  onClose: () => void;
};

export const GoodsReceivingItemsDrawer = observer((props: Props) => {
  const { goodsReceiving, open, onClose } = props;
  const { items, loading } = useGoodsReceivingItems(goodsReceiving);
  const { enrichmentByPositionId, loading: enrichmentLoading } =
    useGoodsReceivingItemEnrichment(items);
  const imageDirectory = goodsReceiving?.directory;

  const [selectedProcess, setSelectedProcess] =
    useState<SIV_GoodsReceiptProcess | null>(null);
  const [processDrawerOpen, setProcessDrawerOpen] = useState(false);

  const [selectedPackage, setSelectedPackage] = useState<SIV_Package | null>(
    null,
  );
  const [packageDrawerOpen, setPackageDrawerOpen] = useState(false);

  const [selectedDeliveryNote, setSelectedDeliveryNote] =
    useState<SIV_DeliveryNotes | null>(null);
  const [deliveryNoteDrawerOpen, setDeliveryNoteDrawerOpen] = useState(false);

  const columns: ColumnType<SIV_GoodsReceivingItem>[] = [
    {
      title: "Pos.",
      dataIndex: "itemNo",
      key: "itemNo",
      width: 70,
    },
    {
      title: "Menge",
      dataIndex: "amount",
      key: "amount",
      width: 80,
      render: (val: number) => val ?? "-",
    },
    {
      title: "Beschreibung",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "WE-Position",
      key: "wePosition",
      width: 150,
      render: (_, record) => {
        const pos = record.goodsReceiptProcessPosition;
        if (!pos) return "-";
        const process = pos.goodsReceiptProcess;
        return (
          <Tag
            color="blue"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setSelectedProcess(process);
              setProcessDrawerOpen(true);
            }}
          >
            <Icon icon="fa:truck" />{" "}
            {process?.no
              ? `${process.no} / ${pos.positionNo}`
              : `Pos. ${pos.positionNo}`}{" "}
            <Icon
              icon="fa:external-link-alt"
              style={{ fontSize: 10, opacity: 0.6 }}
            />
          </Tag>
        );
      },
    },
    {
      title: "Packstücke",
      key: "packages",
      width: 160,
      render: (_, record) => {
        const posId = record.goodsReceiptProcessPosition?.id;
        if (!posId) return "-";
        const entry = enrichmentByPositionId.get(posId);
        if (!entry?.packages.length) return "-";
        return (
          <Space wrap size={4}>
            {entry.packages.map((pkg) => (
              <Tag
                key={pkg.id}
                color="purple"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedPackage(pkg);
                  setPackageDrawerOpen(true);
                }}
              >
                <Icon icon="fa:box" /> {pkg.no}{" "}
                <Icon
                  icon="fa:external-link-alt"
                  style={{ fontSize: 10, opacity: 0.6 }}
                />
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Lieferscheine",
      key: "deliveryNotes",
      width: 160,
      render: (_, record) => {
        const posId = record.goodsReceiptProcessPosition?.id;
        if (!posId) return "-";
        const entry = enrichmentByPositionId.get(posId);
        if (!entry?.deliveryNotes.length) return "-";
        return (
          <Space wrap size={4}>
            {entry.deliveryNotes.map((dn) => (
              <Tag
                key={dn.id}
                color="green"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setSelectedDeliveryNote(dn);
                  setDeliveryNoteDrawerOpen(true);
                }}
              >
                <Icon icon="fa:file-alt" /> {dn.no}{" "}
                <Icon
                  icon="fa:external-link-alt"
                  style={{ fontSize: 10, opacity: 0.6 }}
                />
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Geändert",
      key: "modified",
      width: 130,
      render: (_, record) => (
        <>
          {record.changedAt && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(record.changedAt).format("DD.MM.YYYY")}
              {record.changedBy ? ` von ${getUsername(record.changedBy)}` : ""}
            </Typography.Text>
          )}
        </>
      ),
    },
  ];

  const exportData = items.map((item) => ({
    "Pos.": item.itemNo,
    Menge: item.amount ?? "",
    Beschreibung: item.description || "",
    "WE-Position": (() => {
      const pos = item.goodsReceiptProcessPosition;
      if (!pos) return "";
      const processNo = pos.goodsReceiptProcess?.no;
      return processNo
        ? `${processNo} / ${pos.positionNo}`
        : `Pos. ${pos.positionNo}`;
    })(),
    "Geändert am": item.changedAt
      ? dayjs(item.changedAt).format("DD.MM.YYYY")
      : "",
    "Geändert von": item.changedBy ? getUsername(item.changedBy) : "",
  }));

  return (
    <>
      <Drawer
        title={
          <>
            <Icon icon="fa:box" /> Warenannahme {goodsReceiving?.no} - Inhalte
          </>
        }
        open={open}
        onClose={onClose}
        width={950}
        extra={
          <Space>
            {items.length > 0 && (
              <ExportButton
                data={exportData}
                filename={`Warenannahme_${goodsReceiving?.no || "export"}_Inhalte`}
              />
            )}
          </Space>
        }
      >
        {goodsReceiving && (
          <>
            <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Datum">
                {goodsReceiving.date
                  ? dayjs(goodsReceiving.date).format("DD.MM.YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Lagerort">
                {goodsReceiving.storageLocation ? (
                  <Tag color="blue">{goodsReceiving.storageLocation}</Tag>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Menge">
                {goodsReceiving.amount ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Projekt">
                {goodsReceiving.project || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Lieferant" span={2}>
                {goodsReceiving.supplier || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Beschreibung" span={2}>
                {goodsReceiving.description || "-"}
              </Descriptions.Item>
            </Descriptions>

            <ImageGallery
              directory={imageDirectory}
              title="Bilder"
              height={170}
            />

            <Table
              columns={columns}
              dataSource={items}
              loading={loading || enrichmentLoading}
              rowKey={(record) => record.id!}
              size="small"
              pagination={false}
              scroll={{ x: "max-content" }}
            />

            {!loading && !enrichmentLoading && items.length > 0 && (
              <>
                <Divider />
                <GoodsReceivingFlowChart
                  items={items}
                  enrichmentByPositionId={enrichmentByPositionId}
                />
              </>
            )}
          </>
        )}
      </Drawer>

      <GoodsReceiptProcessPositionsDrawer
        goodsReceiptProcess={selectedProcess}
        open={processDrawerOpen}
        onClose={() => {
          setProcessDrawerOpen(false);
          setSelectedProcess(null);
        }}
      />

      <PackageDetailDrawer
        package={selectedPackage}
        open={packageDrawerOpen}
        onClose={() => {
          setPackageDrawerOpen(false);
          setSelectedPackage(null);
        }}
      />

      <DeliveryNoteDetailDrawer
        deliveryNote={selectedDeliveryNote}
        open={deliveryNoteDrawerOpen}
        onClose={() => {
          setDeliveryNoteDrawerOpen(false);
          setSelectedDeliveryNote(null);
        }}
      />
    </>
  );
});
